import React, { useState, useEffect } from 'react';
import { X, User as UserIcon, Mail, Shield, Key, ToggleLeft, ToggleRight } from 'lucide-react';
import { User, Role } from '../types';
import { userService, UpdateUserDto } from '../services/user.service';
import toast from 'react-hot-toast';

interface UserModalProps {
  user: User | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

export default function UserModal({ user, isOpen, onClose, onSave }: UserModalProps) {
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const [formData, setFormData] = useState<UpdateUserDto & { password?: string }>({
    email: '',
    username: '',
    firstName: '',
    lastName: '',
    roleId: '',
    isActive: true,
    password: '',
  });

  useEffect(() => {
    if (isOpen) {
      if (user) {
        // Editing existing user
        setFormData({
          email: user.email,
          username: user.username,
          firstName: user.firstName,
          lastName: user.lastName,
          roleId: user.Role?.id || '',
          isActive: user.isActive,
        });
      } else {
        // Creating new user
        setFormData({
          email: '',
          username: '',
          firstName: '',
          lastName: '',
          roleId: '',
          isActive: true,
          password: '',
        });
      }
      loadRoles();
    }
  }, [isOpen, user]);

  const loadRoles = async () => {
    try {
      const rolesData = await userService.getAllRoles();
      setRoles(rolesData);
    } catch (error) {
      toast.error('Erro ao carregar perfis');
    }
  };

  const handleSave = async () => {
    // Validation
    if (!user && (!formData.password || !formData.roleId)) {
      toast.error('Senha e perfil são obrigatórios para criar usuário');
      return;
    }

    setLoading(true);
    try {
      if (user) {
        // Update existing user
        await userService.updateUser(user.id, formData);
        toast.success('Usuário atualizado com sucesso');
      } else {
        // Create new user
        await userService.createUser(formData as any);
        toast.success('Usuário criado com sucesso');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error(user ? 'Erro ao atualizar usuário' : 'Erro ao criar usuário');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!user) return;

    if (!window.confirm('Tem certeza que deseja resetar a senha deste usuário?')) return;

    setLoading(true);
    try {
      const result = await userService.resetPassword(user.id);
      toast.success(`Senha temporária: ${result.temporaryPassword}`, {
        duration: 10000,
      });
    } catch (error) {
      toast.error('Erro ao resetar senha');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>
        </div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {user ? 'Detalhes do Usuário' : 'Novo Usuário'}
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Mail className="inline w-4 h-4 mr-1" />
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  autoComplete={user ? "email" : "new-email"}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <UserIcon className="inline w-4 h-4 mr-1" />
                  Username
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  autoComplete={user ? "username" : "new-username"}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sobrenome
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {!user && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Key className="inline w-4 h-4 mr-1" />
                    Senha
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    autoComplete="new-password"
                    required
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Shield className="inline w-4 h-4 mr-1" />
                  Perfil
                </label>
                <select
                  value={formData.roleId}
                  onChange={(e) => setFormData({ ...formData, roleId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  required
                >
                  <option value="">Selecione um perfil</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <button
                  onClick={() => setFormData({ ...formData, isActive: !formData.isActive })}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-md ${
                    formData.isActive
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {formData.isActive ? (
                    <>
                      <ToggleRight className="w-5 h-5" />
                      <span>Ativo</span>
                    </>
                  ) : (
                    <>
                      <ToggleLeft className="w-5 h-5" />
                      <span>Inativo</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {user && (
              <div className="mt-6 border-t pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">
                      Criado em: {new Date(user.createdAt).toLocaleDateString('pt-BR')}
                    </p>
                    {user.lastLogin && (
                      <p className="text-sm text-gray-500">
                        Último login: {new Date(user.lastLogin).toLocaleDateString('pt-BR')}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={handleResetPassword}
                    disabled={loading}
                    className="flex items-center px-3 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 disabled:opacity-50"
                  >
                    <Key className="w-4 h-4 mr-1" />
                    Resetar Senha
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              onClick={handleSave}
              disabled={loading}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
            >
{loading ? 'Salvando...' : (user ? 'Salvar' : 'Criar')}
            </button>
            <button
              onClick={onClose}
              disabled={loading}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}